'use client';

import React, { useEffect, useRef } from 'react';

const principles = [
  {
    title: 'Minimal Data Models',
    desc: 'Planning models should capture what is needed for decision making, not replicate every ERP dimension.',
  },
  {
    title: 'Driver-Based Planning',
    desc: 'Build plans from business drivers — volume, rate, headcount — not from historical line items.',
  },
  {
    title: 'Clear Governance',
    desc: 'Every planning model needs defined ownership, version control, and a documented change process.',
  },
  {
    title: 'Transparent Logic',
    desc: 'Planning calculations should be readable by the business, not only by the technical team.',
  },
  {
    title: 'Continuous Forecasting',
    desc: 'Replace annual budgeting cycles with rolling forecasts that reflect current business reality.',
  },
];

export default function PhilosophySection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-on-scroll').forEach((el) => {
              (el as HTMLElement).classList.add('is-visible');
            });
          }
        });
      },
      { threshold: 0.05 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 px-6 border-b border-border bg-white">
      <div className="max-w-container mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Quote side */}
          <div className="md:col-span-5 reveal-on-scroll">
            <div className="mono-label mb-6">Design Philosophy</div>
            <blockquote
              className="font-heading font-medium text-foreground leading-snug mb-6"
              style={{ fontSize: 'clamp(1.4rem, 2.5vw, 2rem)', letterSpacing: '-0.02em' }}
            >
              "SAC Planning should not replicate ERP complexity. It should enable focused, driver-based planning that supports faster decision making."
            </blockquote>
            <div className="w-8 h-px bg-border-strong" />
          </div>

          {/* Principles */}
          <div className="md:col-span-7">
            <div className="font-heading font-semibold text-foreground text-sm uppercase tracking-wider mb-6 reveal-on-scroll">
              Core Principles
            </div>
            <div className="flex flex-col">
              {principles.map((p, i) => (
                <div
                  key={p.title}
                  className={`reveal-on-scroll reveal-delay-${i + 1} py-5 border-b border-border last:border-0 flex gap-6`}
                >
                  <span className="mono-label mt-0.5 shrink-0 w-6">0{i + 1}</span>
                  <div>
                    <div className="font-heading font-semibold text-foreground text-sm mb-1">
                      {p.title}
                    </div>
                    <p className="text-sm text-muted leading-relaxed font-body">{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}